import apiClient, { setClientLoggingOut } from './client';
import { AUTH_CONSTANTS } from '@/constants/auth';
import { TokenManager } from '@/utils/tokenManager';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface TokenResponse {
  access: string;
  refresh: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
  last_login: string | null;
  profile?: UserProfile;
}

export interface UserProfile {
  phone?: string;
  whatsapp_number?: string;
  birth_date?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  full_address?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  department?: string;
  employment_type?: string;
  contract_type?: string;
  start_date?: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  whatsapp_notifications: boolean;
  push_notifications: boolean;
  backup_email?: string;
  profile_picture?: string;
  notes?: string;
  age?: number;
  created_at: string;
  updated_at: string;
}

class AuthService {
  // Login with username/password
  async login(credentials: LoginCredentials): Promise<TokenResponse> {
    const response = await apiClient.post('/token/', credentials);
    const { access, refresh } = response.data;
    
    // Store tokens
    TokenManager.setToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS, access);
    TokenManager.setToken(AUTH_CONSTANTS.TOKEN_KEYS.REFRESH, refresh);
    
    return { access, refresh };
  }

  // Login with API token
  async loginWithToken(token: string): Promise<void> {
    TokenManager.setToken(AUTH_CONSTANTS.TOKEN_KEYS.API, token);
  }

  // Logout
  logout(): void {
    console.log('=== AUTHSERVICE: Starting logout ===');
    
    // Debug: Show all keys before clearing
    TokenManager.debugLocalStorage();
    
    // Signal to client.ts that logout is running
    console.log('Setting client logging out flag...');
    try { setClientLoggingOut(true); } catch { /* noop */ }
    
    // Use centralized token management
    TokenManager.removeAllTokens();
    
    console.log('=== AUTHSERVICE: All tokens cleared ===');
    
    // Reset client state after delay
    setTimeout(() => { 
      try { setClientLoggingOut(false); } catch { /* noop */ } 
    }, AUTH_CONSTANTS.CLIENT_RESET_DELAY);
  }

  // Get current user
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get('/me/');
    return response.data;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    // Debug: Show all localStorage keys
    TokenManager.debugLocalStorage();
    
    // Check if any authentication token exists
    const hasToken = TokenManager.hasAnyToken();
    
    if (!hasToken) {
      console.log('No authentication tokens found');
      return false;
    }
    
    console.log('Authentication tokens found, will validate...');
    return true;
  }

  // Validate token WITHOUT calling logout (verhindert Rekursion)
  async validateToken(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch (error) {
      console.log('Token validation failed:', error);
      // NICHT logout() aufrufen - das verursacht Rekursion!
      // Tokens manuell löschen
      TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS);
      TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.REFRESH);
      TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.API);
      return false;
    }
  }

  // Get stored token
  getToken(): string | null {
    return TokenManager.getToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS) || 
           TokenManager.getToken(AUTH_CONSTANTS.TOKEN_KEYS.API);
  }

  // Refresh token
  async refreshToken(): Promise<TokenResponse> {
    const refreshToken = TokenManager.getToken(AUTH_CONSTANTS.TOKEN_KEYS.REFRESH);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post('/token/refresh/', {
      refresh: refreshToken,
    });

    const { access, refresh } = response.data;
    TokenManager.setToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS, access);
    TokenManager.setToken(AUTH_CONSTANTS.TOKEN_KEYS.REFRESH, refresh);

    return { access, refresh };
  }
}

export const authService = new AuthService();
export default authService;